/**
 * useChartSize — Mide el contenedor de un gráfico con `ResizeObserver`.
 *
 * QUÉ HACE: devuelve un ref para el contenedor y el ancho/alto de su
 *   **content-box** (sin padding) en px, actualizados cuando cambia el tamaño de
 *   la tarjeta (cuadrícula movible, rotación, zoom…). Medir sin padding es
 *   clave: los ejes y la leyenda deciden sus saltos con el ancho real del
 *   lienzo, no con el del envoltorio (`px-2 sm:px-4` inflaba hasta 32 px).
 *   Fallback a `window.resize` si no hay `ResizeObserver`.
 * QUÉ INVOCA: ResizeObserver (nativo) y React.
 * QUÉ EXPORTA: useChartSize (+ ChartSize).
 * QUÉ NO DEBE HACER: no conoce gráficos ni datos; solo mide.
 */
import { useEffect, useRef, useState } from "react";

export interface ChartSize {
  width: number;
  height: number;
}

interface UseChartSizeOptions {
  /**
   * Mide el **elemento padre** en vez del propio. Necesario para piezas que
   * llenan la tarjeta pero cuyo contenido no tiene una altura definida (p. ej.
   * el estado vacío: con `height:100%` sobre un padre flex el alto puede
   * resolverse a `auto`, y el gráfico no reaccionaría al redimensionado en
   * alto). Midiendo al padre se obtiene siempre el alto real disponible.
   */
  observeParent?: boolean;
}

export function useChartSize<T extends HTMLElement = HTMLDivElement>(
  options: UseChartSizeOptions = {},
) {
  const { observeParent = false } = options;
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<ChartSize>({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const target = observeParent ? element.parentElement : element;
    if (!target) return;

    const update = () => {
      setSize((previous) => {
        const style = window.getComputedStyle(target);
        const paddingX =
          (parseFloat(style.paddingLeft) || 0) +
          (parseFloat(style.paddingRight) || 0);
        const paddingY =
          (parseFloat(style.paddingTop) || 0) +
          (parseFloat(style.paddingBottom) || 0);
        const width = Math.max(0, target.clientWidth - paddingX);
        const height = Math.max(0, target.clientHeight - paddingY);
        if (previous.width === width && previous.height === height) {
          return previous;
        }
        return { width, height };
      });
    };

    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(target);
    return () => observer.disconnect();
  }, [observeParent]);

  return { ref, width: size.width, height: size.height };
}
